/**
 * Custom hooks extracted from `PostStreamReviewScreen` to keep the screen
 * component readable and to isolate the subtler effect-timing logic for unit
 * testing.
 */
import { useCallback, useEffect, useRef } from 'react';

import { bind } from 'bind-event-listener';

import { getDocument } from '@atlaskit/browser-apis';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { openPostStreamReviewModal } from '../../../../../editor-actions/actions';

/**
 * Close the modal when the user clicks anywhere outside the toolbar.
 *
 * We listen on `pointerdown` (rather than `click`) so the dismissal fires
 * before the editor's own selection handler runs — clicking into the editor
 * body to keep editing should still close the toolbar cleanly without the
 * toolbar momentarily competing for the same selection.
 *
 * The listener is attached at the document level in the capture phase so
 * descendant `stopPropagation()` calls (e.g. from popups rendered inside the
 * prompt input) don't accidentally suppress the outside-click detection. We
 * then explicitly skip events whose target is contained by the toolbar root
 * (`screenRef`) and events inside any element carrying the
 * `data-post-stream-review-portal` escape-hatch attribute, so portalled
 * descendants (Popup, Tooltip, Menu) that render to `document.body` are
 * treated as "inside" the toolbar.
 */
export function useOutsideClickClose(
	screenRef: React.RefObject<HTMLElement | null>,
	close: () => void,
): void {
	useEffect(() => {
		const doc = getDocument();
		if (!doc) {
			return;
		}
		const handlePointerDown = (event: PointerEvent) => {
			const target = event.target;
			if (!(target instanceof Node)) {
				return;
			}
			if (screenRef.current?.contains(target)) {
				return;
			}
			if (target instanceof Element && target.closest('[data-post-stream-review-portal]')) {
				return;
			}
			close();
		};
		const unbind = bind(doc, {
			type: 'pointerdown',
			listener: handlePointerDown,
			options: { capture: true },
		});
		return unbind;
	}, [screenRef, close]);
}

/**
 * Auto-hide the diff overlay when the screen unmounts (modal closed for any
 * reason — `endExperience`, slice replace, full session reset). Without this
 * the diff decorations would linger on the page after the toolbar goes away.
 *
 * IMPORTANT: this effect MUST only depend on the empty array so the cleanup
 * runs exclusively at unmount. An earlier version listed `coreActions` and
 * `hideDiff` as deps, but those reference identities can change across
 * renders — and `coreActions.execute(showDiff(...))` itself triggers a
 * re-render of the parent component via the shared plugin-state subscription,
 * which invalidated the deps and ran the cleanup mid-render, immediately
 * hiding the diff the user had just asked to see (the View Changes button
 * effectively did nothing visible).
 *
 * To keep the cleanup correct (it must call the *latest* `hideDiff` command
 * at unmount time) we capture the action + command into a ref that we keep
 * up to date on every render, and have the unmount cleanup read from the
 * ref. This decouples cleanup-timing from dep-identity changes.
 */
export function useHideDiffOnUnmount(
	coreActions: { execute: (cmd: never) => unknown } | undefined,
	hideDiff: unknown | undefined,
): void {
	const hideOnUnmountRef = useRef<(() => void) | null>(null);
	hideOnUnmountRef.current = () => {
		if (coreActions && hideDiff) {
			coreActions.execute(hideDiff as never);
		}
	};
	useEffect(() => {
		return () => {
			hideOnUnmountRef.current?.();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
}

/**
 * Segment navigation: Prev/Next re-anchor the modal at a different segment
 * by dispatching a fresh `openPostStreamReviewModal` for the new segment
 * index in the next microtask. `createOpenAIModalCommand` issues a new
 * `SHOW_MODAL` with a decoration anchored at the new segment's
 * `[startPos, endPos]`, and the decoration plugin's `apply()` swaps the old
 * decoration for the new one — ModalRegion re-renders against the new anchor
 * element without us needing to tear down the experience.
 *
 * NOTE: an earlier iteration of this code called `endExperience(...)` here
 * in response to a code-review suggestion. That turned out to fully close
 * the modal (because `endExperience` is the same prop the close button uses)
 * and the racing reopen never won, so Prev/Next felt like "close". The
 * decoration-plugin's `SHOW_MODAL` flow already handles re-anchoring on its
 * own.
 */
export function useSegmentNavigation(
	editorView: EditorView | undefined,
	segmentIndex: number,
	totalSegments: number,
): {
	canGoNext: boolean;
	canGoPrevious: boolean;
	goToNext: () => void;
	goToPrevious: () => void;
} {
	const goToSegment = useCallback(
		(newIndex: number) => {
			if (!editorView) {
				return;
			}
			Promise.resolve().then(() => {
				if (editorView && !editorView.isDestroyed) {
					openPostStreamReviewModal({
						editorView,
						segmentData: { segmentIndex: newIndex },
					});
				}
			});
		},
		[editorView],
	);

	const canGoPrevious = segmentIndex > 0;
	const canGoNext = segmentIndex < totalSegments - 1;

	const goToPrevious = useCallback(() => {
		if (!canGoPrevious) {
			return;
		}
		goToSegment(segmentIndex - 1);
	}, [canGoPrevious, segmentIndex, goToSegment]);

	const goToNext = useCallback(() => {
		if (!canGoNext) {
			return;
		}
		goToSegment(segmentIndex + 1);
	}, [canGoNext, segmentIndex, goToSegment]);

	return { canGoNext, canGoPrevious, goToNext, goToPrevious };
}
